// Resizes a textarea to fit its content - runs on mount (as a ref callback,
// so text loaded from the server is fully sized on arrival) and on every
// keystroke (via onChange), so the full value is always visible with no
// inner scrollbar - up to the ceiling below.
//
// EXTRACTED, NOT COPIED (F17, 2026-08-22). This lived as a module-local
// const in StepQuestions.jsx until the Lines editor's description field
// needed the same growth behaviour. Duplicating five lines would have
// created an unregistered mirror pair of exactly the kind CLAUDE.md's
// registry exists to prevent - the same reasoning the same ruling applied
// to the colSpan literal in LineItemsEditor.jsx. Both call sites now
// import this one function.
//
// BASE AND CEILING (item 8, 2026-09-03). A growing textarea starts at TWO
// rows and grows with its content to a STATED ceiling, after which it
// scrolls inside itself. The ceiling is a CSS max-height, so this helper
// stays a pure "height = content" write: the browser clamps it, and
// overflow-y-auto restores the scrollbar exactly when the clamp bites.
// The Tailwind class must be a literal for the JIT to emit it, so the
// pixel value appears twice below; lib/autoResizeTextarea.test.js asserts
// the two agree against the same literal.
import { useEffect, useLayoutEffect, useRef } from 'react';

export const TEXTAREA_BASE_ROWS = 2;
export const TEXTAREA_MAX_HEIGHT_PX = 400;
export const GROWING_TEXTAREA_CLASS = 'resize-none overflow-y-auto max-h-[400px]';

// THE CLAMP IS WRITTEN HERE TOO (2026-09-04). The CSS max-height still
// clamps in the browser, but a helper that writes the raw scrollHeight is
// unobservable under jsdom (scrollHeight is 0 there) and a test could not
// tell a helper that respects the ceiling from one that does not. Writing
// min(scrollHeight, ceiling) makes the ceiling a property of the helper
// itself, so a fixture that defines scrollHeight can assert growth to
// five lines AND the stop at 400px (lib/autoResizeTextarea.test.js).
export const autoResizeTextarea = (el) => {
  if (!el) return;
  el.style.height = 'auto';
  el.style.height = `${Math.min(el.scrollHeight, TEXTAREA_MAX_HEIGHT_PX)}px`;
};

// ONE MEASUREMENT IS NOT ENOUGH (PROC-000412, 2026-09-04). The Lines
// editor's description field measured itself once, in a ref callback at
// mount, and never again. Inside a table whose column widths settle AFTER
// mount (the category select and the location column claim width once
// their data arrives), the description column narrows, the text rewraps
// to one more line, and the height set for the wider column now hides the
// last line - "…to be confirmed per vendor product" with no "proposal."
// on the two longest descriptions, while shorter ones happened to keep
// their line count. Not a truncation: the stored text was complete.
//
// This hook measures on mount, whenever the value changes (text that
// arrives after mount, or is replaced programmatically), whenever the
// element's own width changes (ResizeObserver - the table settling, a
// window resize, a sidebar opening), and on window resize as a fallback
// where ResizeObserver is absent (jsdom). Pair it with GROWING_TEXTAREA_CLASS
// so the CSS ceiling and the scrollbar agree with the helper's clamp.
export const useAutoResizeTextarea = (value) => {
  const ref = useRef(null);
  useLayoutEffect(() => {
    autoResizeTextarea(ref.current);
  }, [value]);
  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const remeasure = () => autoResizeTextarea(el);
    let observer;
    if (typeof ResizeObserver !== 'undefined') {
      observer = new ResizeObserver(remeasure);
      observer.observe(el);
    }
    window.addEventListener('resize', remeasure);
    return () => {
      if (observer) observer.disconnect();
      window.removeEventListener('resize', remeasure);
    };
  }, []);
  return ref;
};

export default autoResizeTextarea;
